import { createFileRoute } from "@tanstack/react-router";
import { TableLoaderRow } from "@/components/ui/loader";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useFar } from "@/features/finance/api";
import { currentFy, fyOptions } from "@/features/finance/fy";
import { inr } from "@/features/finance/format";
import { Printer } from "lucide-react";
import { ReportExportButton } from "@/features/reports/export/ReportExportButton";
import { useBranch } from "@/context/BranchContext";

export const Route = createFileRoute("/_authenticated/finance/fixed-asset-register")({
  component: FarPage,
});

function FarPage() {
  const [fy, setFy] = useState(currentFy());
  const { currentBranchId } = useBranch();
  const { data, isLoading } = useFar(fy, currentBranchId);
  const rows = data ?? [];

  const totals = rows.reduce(
    (a, r) => ({
      gross: a.gross + Number(r.gross_block ?? 0),
      add: a.add + Number(r.additions ?? 0),
      del: a.del + Number(r.deletions ?? 0),
      dep: a.dep + Number(r.dep_this_fy ?? 0),
      nbv: a.nbv + Number(r.nbv_closing ?? 0),
    }),
    { gross: 0, add: 0, del: 0, dep: 0, nbv: 0 },
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Fixed Asset Register</h1>
          <p className="text-sm text-muted-foreground">FY {fy} · {rows.length} assets</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={fy} onValueChange={setFy}>
            <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
            <SelectContent>{fyOptions().map((y) => <SelectItem key={y} value={y}>FY {y}</SelectItem>)}</SelectContent>
          </Select>
          <ReportExportButton
            reportKey="R-11"
            title={`FAR ${fy}`}
            filename={`fixed-asset-register-${fy}`}
            rows={() => rows.map((r) => ({
              "Asset Code": r.asset_code,
              "Asset Name": r.asset_name,
              "Category": r.category_name ?? "",
              "Capitalisation Date": r.capitalisation_date ?? "",
              "Gross Block": Number(r.gross_block ?? 0),
              "Additions": Number(r.additions ?? 0),
              "Deletions": Number(r.deletions ?? 0),
              "Accum. Dep Opening": Number(r.accum_dep_opening ?? 0),
              "Dep This FY": Number(r.dep_this_fy ?? 0),
              "Accum. Dep Closing": Number(r.accum_dep_closing ?? 0),
              "NBV Closing": Number(r.nbv_closing ?? 0),
            }))}
            requiredColumns={["Asset Code"]}
            variant="outline"
            size="default"
            disabled={!rows.length}
          />
          <Button variant="outline" onClick={() => window.print()}><Printer className="mr-1 h-4 w-4" />PDF</Button>
        </div>
      </div>
      <Card>
        <CardHeader><CardTitle className="text-base">Register</CardTitle></CardHeader>
        <CardContent>
          <div className="max-h-[70vh] overflow-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Code</TableHead><TableHead>Asset</TableHead><TableHead>Category</TableHead>
                <TableHead>Cap. Date</TableHead>
                <TableHead className="text-right">Gross Block</TableHead>
                <TableHead className="text-right">Additions</TableHead>
                <TableHead className="text-right">Deletions</TableHead>
                <TableHead className="text-right">Acc. Dep Open</TableHead>
                <TableHead className="text-right">Dep FY</TableHead>
                <TableHead className="text-right">Acc. Dep Close</TableHead>
                <TableHead className="text-right">NBV Close</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {isLoading && <TableLoaderRow colSpan={11} />}
                {rows.map((r) => (
                  <TableRow key={r.asset_id}>
                    <TableCell className="font-mono text-xs">{r.asset_code}</TableCell>
                    <TableCell>{r.asset_name}</TableCell>
                    <TableCell>{r.category_name ?? "—"}</TableCell>
                    <TableCell>{r.capitalisation_date ?? "—"}</TableCell>
                    <TableCell className="text-right">{inr(r.gross_block)}</TableCell>
                    <TableCell className="text-right">{inr(r.additions)}</TableCell>
                    <TableCell className="text-right">{inr(r.deletions)}</TableCell>
                    <TableCell className="text-right">{inr(r.accum_dep_opening)}</TableCell>
                    <TableCell className="text-right">{inr(r.dep_this_fy)}</TableCell>
                    <TableCell className="text-right">{inr(r.accum_dep_closing)}</TableCell>
                    <TableCell className="text-right font-medium">{inr(r.nbv_closing)}</TableCell>
                  </TableRow>
                ))}
                {rows.length > 0 && (
                  <TableRow className="bg-muted/40 font-semibold">
                    <TableCell colSpan={4}>Totals</TableCell>
                    <TableCell className="text-right">{inr(totals.gross)}</TableCell>
                    <TableCell className="text-right">{inr(totals.add)}</TableCell>
                    <TableCell className="text-right">{inr(totals.del)}</TableCell>
                    <TableCell className="text-right">—</TableCell>
                    <TableCell className="text-right">{inr(totals.dep)}</TableCell>
                    <TableCell className="text-right">—</TableCell>
                    <TableCell className="text-right">{inr(totals.nbv)}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
